import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Calendar, Clock, ArrowLeft, ArrowRight, Phone, Mail, MapPin, MessageSquare, Menu, X, BookOpen } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Chatbot } from '@/components/Chatbot';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BlogPage = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Blog images based on keywords
  const getBlogImage = (title) => {
    const titleLower = title.toLowerCase();
    if (titleLower.includes('aws') || titleLower.includes('cloud')) {
      return 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=600';
    }
    if (titleLower.includes('security') || titleLower.includes('cyber')) {
      return 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=600';
    }
    if (titleLower.includes('ccna') || titleLower.includes('cisco') || titleLower.includes('network')) {
      return 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600';
    }
    if (titleLower.includes('azure') || titleLower.includes('microsoft')) {
      return 'https://images.unsplash.com/photo-1667372393119-3d4c48d07fc9?w=600';
    }
    if (titleLower.includes('comptia') || titleLower.includes('a+')) {
      return 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=600';
    }
    return 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=600';
  };

  return (
    <>
      <Helmet>
        <title>IT Certification Blog | Train Secure - AWS, CompTIA, Cisco Training UK</title>
        <meta name="description" content="Expert insights on IT certifications, career advice, and training tips. AWS, CompTIA A+, Security+, Cisco CCNA, Azure certification guides and career resources." />
        <link rel="canonical" href="https://www.trainsecure.co.uk/blog" />
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white">
        {/* Header */}
        <header className="border-b border-gray-800 bg-[#0a0e1a]/95 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="Train Secure"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-base md:text-lg font-bold">Train Secure</h1>
                <p className="text-xs text-cyan-400">North London College</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/#contact')} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold">
                Get in Touch
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 hover:bg-slate-800 rounded-lg"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="bg-[#0f1420] border-t border-gray-800 px-4 py-4 space-y-3">
              <a href="/#courses" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Courses</a>
              <a href="/#testimonials" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Reviews</a>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Contact</a>
              <a href="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Blog</a>
              <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 py-2">
                <MessageSquare className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          )}
        </header>

        {/* Hero */}
        <section className="py-16 px-4 bg-gradient-to-b from-[#111827] to-[#0a0e1a] relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-cyan-500 rounded-full filter blur-[120px]"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 rounded-full filter blur-[120px]"></div>
          </div>
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="inline-flex items-center gap-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-2 mb-6">
              <BookOpen className="w-4 h-4 text-cyan-400" />
              <span className="text-cyan-400 text-sm font-medium">IT Career Insights</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">IT Certification Blog</h1>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Expert insights, career advice, and training tips to help you succeed in IT
            </p>
          </div>
        </section>

        {/* Blog Posts */}
        <section className="py-12 px-4">
          <div className="max-w-6xl mx-auto">
            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
              </div>
            ) : posts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {posts.map((post) => (
                  <Card key={post.id} className="bg-[#111827] border-gray-800 overflow-hidden hover:border-cyan-500/50 transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/10 group">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image_url || getBlogImage(post.title)} 
                        alt={post.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" 
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#111827] to-transparent"></div>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {post.read_time} min read
                        </span>
                      </div>
                      <h2 className="text-xl font-bold text-white mb-3 line-clamp-2 group-hover:text-cyan-400 transition-colors">{post.title}</h2>
                      <p className="text-gray-400 mb-4 line-clamp-3">{post.excerpt}</p>
                      <Link to={`/blog/${post.slug}`} className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-medium group/link">
                        Read More 
                        <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                      </Link>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 bg-[#111827] rounded-2xl border border-gray-800">
                <div className="w-16 h-16 bg-cyan-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <BookOpen className="w-8 h-8 text-cyan-400" />
                </div>
                <h2 className="text-2xl font-bold mb-4">Blog Coming Soon</h2>
                <p className="text-gray-400 mb-8 max-w-md mx-auto">We're working on expert content to help you with your IT certification journey.</p>
                <a href="/#courses">
                  <Button className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold">View Our Courses</Button>
                </a>
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4 bg-gradient-to-r from-cyan-900/20 to-blue-900/20 border-y border-cyan-500/10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Start Your IT Career?</h2>
            <p className="text-gray-300 mb-8">Get certified in as little as 2-4 days with our intensive bootcamps. Exam voucher included.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/#courses">
                <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold px-8">
                  View Courses
                </Button>
              </a>
              <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="lg" className="border-green-500 text-green-400 hover:bg-green-500/10 px-8">
                  <MessageSquare className="w-5 h-5 mr-2" /> WhatsApp Us
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="border-t border-gray-800 py-12 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-8">
              <div>
                <h4 className="font-bold mb-4">Train Secure</h4>
                <p className="text-gray-400 text-sm">North London College - Pearson VUE Test Centre. Industry-leading IT certification training in London.</p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Contact</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> 0208 808 8008</li>
                  <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> info@trainsecure.co.uk</li>
                  <li className="flex items-center gap-2"><MessageSquare className="w-4 h-4" /> +44 7435 177049</li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4">Location</h4>
                <p className="text-gray-400 text-sm flex items-start gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-1" />
                  376 High Road, Tottenham, London N17 9HY
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4">Popular Courses</h4>
                <ul className="space-y-2 text-gray-400 text-sm">
                  <li><a href="/courses/aws-cloud-practitioner" className="hover:text-cyan-400">AWS Cloud Practitioner</a></li>
                  <li><a href="/courses/comptia-security-plus" className="hover:text-cyan-400">CompTIA Security+</a></li>
                  <li><a href="/courses/cisco-ccna" className="hover:text-cyan-400">Cisco CCNA</a></li>
                </ul>
              </div>
            </div>
            <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-500 text-sm">
              © 2025 Train Secure - North London College. All rights reserved.
            </div>
          </div>
        </footer>

        <Chatbot />
      </div>
    </>
  );
};

export const BlogPostPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchPost();
  }, [slug]);

  const fetchPost = async () => {
    try {
      const response = await axios.get(`${API}/blog/posts/${slug}`);
      setPost(response.data);
    } catch (error) {
      console.error('Failed to fetch post:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-400"></div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-[#0a0e1a] text-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Post Not Found</h1>
          <Link to="/blog" className="text-cyan-400 hover:text-cyan-300">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{post.title} | Train Secure Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://www.trainsecure.co.uk/blog/${slug}`} />
        
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:type" content="article" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": post.excerpt,
            "datePublished": post.published_at,
            "author": {
              "@type": "Organization",
              "name": "Train Secure"
            }
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0e1a] text-white">
        {/* Header */}
        <header className="border-b border-gray-800 bg-[#0a0e1a]/95 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
            <a href="/" className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_nlc-bootcamp/artifacts/ipcogjr5_NLC-%20TRAIN%20SECURE%20LOGO.jpg"
                alt="Train Secure"
                className="h-10 w-10 rounded-full object-cover"
              />
              <div>
                <h1 className="text-base md:text-lg font-bold">Train Secure</h1>
                <p className="text-xs text-cyan-400">North London College</p>
              </div>
            </a>
            <div className="flex items-center gap-3">
              <Button onClick={() => navigate('/#contact')} size="sm" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold">
                Get in Touch
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="text-white p-2 hover:bg-slate-800 rounded-lg"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
          
          {/* Menu Dropdown */}
          {mobileMenuOpen && (
            <div className="bg-[#0f1420] border-t border-gray-800 px-4 py-4 space-y-3">
              <a href="/#courses" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Courses</a>
              <a href="/#testimonials" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Reviews</a>
              <a href="/#contact" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Contact</a>
              <a href="/blog" onClick={() => setMobileMenuOpen(false)} className="block text-gray-300 hover:text-cyan-400 py-2">Blog</a>
              <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-green-400 py-2">
                <MessageSquare className="w-4 h-4" /> WhatsApp Us
              </a>
            </div>
          )}
        </header>

        {/* Article */}
        <article className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <Link to="/blog" className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 mb-8 group">
              <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" /> Back to Blog
            </Link>
            
            <h1 className="text-3xl md:text-4xl font-bold mb-6">{post.title}</h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-400 mb-8 pb-8 border-b border-gray-800">
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.read_time} min read
              </span>
            </div>

            {post.image_url && (
              <img src={post.image_url} alt={post.title} className="w-full rounded-xl mb-8" />
            )}

            <div 
              className="prose prose-invert prose-cyan max-w-none prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-a:text-cyan-400 prose-li:text-gray-300"
              dangerouslySetInnerHTML={{ __html: post.content }}
            />

            {/* CTA */}
            <div className="mt-12 p-8 bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-2xl border border-cyan-500/20 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Get Certified?</h3>
              <p className="text-gray-300 mb-6">Join our expert-led bootcamps in London. Small classes, exam voucher included.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a href="/#courses">
                  <Button size="lg" className="bg-cyan-500 hover:bg-cyan-600 text-slate-900 font-semibold">View Our Courses</Button>
                </a>
                <a href="https://wa.me/447435177049" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="lg" className="border-green-500 text-green-400 hover:bg-green-500/10">
                    <MessageSquare className="w-5 h-5 mr-2" /> WhatsApp Us
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </article>

        {/* Footer */}
        <footer className="border-t border-gray-800 py-12 px-4 mt-12">
          <div className="max-w-7xl mx-auto text-center">
            <p className="text-gray-500 text-sm">© 2025 Train Secure - North London College. All rights reserved.</p>
          </div>
        </footer>

        <Chatbot />
      </div>
    </>
  );
};

export default BlogPage;
